import { Input, Typography } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import { WorkspaceCard } from "../../LeadWorkspacePage/cards/WorkspaceCard.tsx"
import { WorkspaceCardValue } from "../../LeadWorkspacePage/cards/WorkspaceCardValue.tsx"
import { type LeadPatchSender } from "../../LeadWorkspacePage/lead-workspace-patch.ts"

const { Paragraph } = Typography

interface InternalNotesValues {
  internal_notes: string | null
}

interface LeadViewInternalNotesProps {
  notes?: string | null
  canEdit?: boolean
  /** Two tabs may show this card at once; the guard registry keys on the id. */
  cardId?: string
  /** Opt-in only — lets a grid-stretched caller (Overview) match its sibling's height. */
  className?: string
  onPatch: LeadPatchSender
}

export function LeadViewInternalNotes({
  notes,
  canEdit,
  cardId = "internal-notes",
  className,
  onPatch
}: LeadViewInternalNotesProps) {
  const { t } = useTranslation()
  const title = t("leadWorkspace.cards.internalNotes.title")

  return (
    <WorkspaceCard<InternalNotesValues>
      cardId={cardId}
      title={title}
      className={className}
      canEdit={canEdit}
      values={{ internal_notes: notes ?? null }}
      onSave={onPatch}
      renderEdit={({ values, setValues, saving }) => (
        <Input.TextArea
          autoFocus
          rows={4}
          disabled={saving}
          aria-label={title}
          value={values.internal_notes ?? ""}
          onChange={(event) =>
            setValues({ internal_notes: event.target.value })
          }
        />
      )}
    >
      <WorkspaceCardValue
        value={notes}
        placeholder={t("leadWorkspace.cards.internalNotes.empty")}
      >
        {/* Expanding in place pushed the page past everything below it — a
            long note is read in its own scrollport instead. Focusable so the
            keyboard reaches the part of the note below the fold, which the
            "more" toggle used to cover: the tab stop is unconditional, since
            telling a short note from a scrolling one needs measurement. */}
        <div
          tabIndex={0}
          role={"region"}
          aria-label={title}
          className={
            "tailwind-scroll max-h-56 overflow-y-auto overflow-x-hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
          }
        >
          <Paragraph className={"mb-0 whitespace-pre-wrap break-words"}>
            {notes}
          </Paragraph>
        </div>
      </WorkspaceCardValue>
    </WorkspaceCard>
  )
}
